import type { useGateways } from '@/gateways/GatewayProvider';
import type { Pagination } from '@/shared/types';

/**
 * Field descriptors — which control renders and what value type it produces —
 * are the shared vocabulary between the condition/action registries and the
 * `FieldInput` renderer.
 *
 * A discriminated union: each variant carries only its own props, so invalid
 * combinations — `options` on a number, a `query` on a datetime — don't compile.
 * Build descriptors through the helper constructors below rather than by hand.
 */
export type FieldDescriptor =
  | TextDescriptor
  | NumberDescriptor
  | SelectDescriptor
  | MultiSelectDescriptor
  | TagsDescriptor
  | EntitySelectDescriptor
  | DateTimeDescriptor
  | NoneDescriptor;

export type SelectOption = {
  value: string;
  label: string;
};

export type EntityQueryInput = {
  search?: string;
  take?: number;
  cursor?: string;
};

export type EntityQueryResult = {
  items: unknown[];
  pagination?: Pagination | null;
};

/**
 * Resolves the options shown by an entity-select. The shape matches the app's
 * autocomplete query hook and `fetchAllPages`, so gateway query methods can be
 * passed directly — driven by the search term in autocomplete mode, or paged to
 * exhaustion when the descriptor sets `preloadAll`.
 */
export type EntityQuery = (
  gateways: ReturnType<typeof useGateways>,
  input: EntityQueryInput | undefined,
  options: { signal?: AbortSignal },
) => Promise<EntityQueryResult>;

/** Free text. Value: `string`. */
export type TextDescriptor = { kind: 'text' };

/** A number, optionally constrained to integers. Value: `number`. */
export type NumberDescriptor = { kind: 'number'; integer: boolean };

/** One choice from a fixed list. Value: `string`. */
export type SelectDescriptor = { kind: 'select'; options: SelectOption[] };

/** Several choices from a fixed list. Value: `string[]`. */
export type MultiSelectDescriptor = { kind: 'multi-select'; options: SelectOption[] };

/** Free-form, de-duplicated string entry. Value: `string[]`. */
export type TagsDescriptor = { kind: 'tags' };

/**
 * One or more choices fetched from a gateway as the user types. Value: the
 * `valueKey` field of the chosen item(s) — `string` when single, `string[]`
 * when `multi`.
 */
export type EntitySelectDescriptor = {
  kind: 'entity-select';
  query: EntityQuery;
  /** React Query cache parent key, scoping the autocomplete results. */
  parentKey: string;
  /** Item field used as the stored value. */
  valueKey: string;
  /** Item field shown to the user; falls back to `valueKey`. */
  labelKey?: string;
  multi: boolean;
  /** Preload all options and use local combobox filtering instead of server search. */
  preloadAll: boolean;
};

/**
 * Backend `date` condition properties are timestamps, so the value is a full
 * ISO datetime string, never date-only.
 */
export type DateTimeDescriptor = { kind: 'datetime' };

/** No value input — for operators like `IS_EMPTY` that take no operand. */
export type NoneDescriptor = { kind: 'none' };

export const text = (): TextDescriptor => ({ kind: 'text' });

export const number = ({ integer = false }: { integer?: boolean } = {}): NumberDescriptor => ({
  kind: 'number',
  integer,
});

export const select = ({ options }: { options: SelectOption[] }): SelectDescriptor => ({
  kind: 'select',
  options,
});

export const multiSelect = ({ options }: { options: SelectOption[] }): MultiSelectDescriptor => ({
  kind: 'multi-select',
  options,
});

export const tags = (): TagsDescriptor => ({ kind: 'tags' });

export const entitySelect = ({
  query,
  parentKey,
  valueKey,
  labelKey,
  multi = false,
  preloadAll = false,
}: {
  query: EntityQuery;
  parentKey: string;
  valueKey: string;
  labelKey?: string;
  multi?: boolean;
  preloadAll?: boolean;
}): EntitySelectDescriptor => ({
  kind: 'entity-select',
  query,
  parentKey,
  valueKey,
  labelKey,
  multi,
  preloadAll,
});

export const datetime = (): DateTimeDescriptor => ({ kind: 'datetime' });

export const none = (): NoneDescriptor => ({ kind: 'none' });

export const optionsFromValues = (values: readonly string[]): SelectOption[] =>
  values.map((value) => ({ value, label: value }));
